// ─── Messaging service (ops console) ──────────────────────────────────────────
// Backs the MessagesPage compose + inbox, and the header unread bell. Talks to the
// controllers at /api/v1/messaging/* (individual direct sends, group fan-out, inbox,
// unread-count, and the recipient picker search).

import apiClient from './client';
import { unwrap, ApiResponse } from './apiHelpers';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface DirectMessageResultDto {
  messagePublicId: string;
  threadPublicId: string;
  recipientUserPublicId: string;
  sender: string;
  subject?: string | null;
  body: string;
  sentAtUtc: string;
}

export interface GroupMessageResultDto {
  recipientCount: number;
}

export interface InboxMessageDto {
  id: string;              // UserMessage.PublicId
  from: string;            // Sender display name
  kind: string;            // 'direct' | 'system' | 'info' | 'job-assigned' | 'job-reassigned-away'
  time: string;            // ISO 8601 UTC
  subject?: string | null;
  body: string;
  read: boolean;
  relatedEntityType?: string | null;
  relatedEntityPublicId?: string | null;
  threadPublicId?: string | null;
  senderUserPublicId?: string | null;
}

export interface UnreadCountDto {
  count: number;
}

export interface RecipientDto {
  userPublicId: string;
  displayName: string;
  email: string;
  role: 'Driver' | 'PA';
}

export interface SendDirectPayload {
  recipientUserPublicId: string;
  body: string;
  subject?: string;
  threadPublicId?: string;
}

export interface SendGroupPayload {
  allDrivers: boolean;
  allPas: boolean;
  recipientUserPublicIds?: string[];
  body: string;
  subject?: string;
}

export interface ThreadSummaryDto {
  threadPublicId: string;
  counterpartyUserPublicId: string;
  counterpartyName: string;
  counterpartyRole: 'Driver' | 'PA' | 'Ops';
  lastMessagePreview: string;
  lastMessageSubject?: string | null;
  lastMessageAtUtc: string;
  lastMessageWasFromMe: boolean;
  unreadCount: number;
}

export interface ChatMessageDto {
  messagePublicId: string;
  direction: 'sent' | 'received';
  from: string;
  subject?: string | null;
  body: string;
  sentAtUtc: string;
  isRead: boolean;
  senderUserPublicId?: string | null;
}

// ── Service ────────────────────────────────────────────────────────────────────

export const messagingService = {
  async sendDirect(payload: SendDirectPayload): Promise<DirectMessageResultDto> {
    const res = await apiClient.post<ApiResponse<DirectMessageResultDto>>(
      '/messaging/direct', payload,
    );
    return unwrap(res.data);
  },

  async sendGroup(payload: SendGroupPayload): Promise<GroupMessageResultDto> {
    const res = await apiClient.post<ApiResponse<GroupMessageResultDto>>(
      '/messaging/send', payload,
    );
    return unwrap(res.data);
  },

  async listInbox(): Promise<InboxMessageDto[]> {
    const res = await apiClient.get<ApiResponse<InboxMessageDto[]>>('/messaging/inbox');
    return unwrap(res.data);
  },

  async unreadCount(): Promise<UnreadCountDto> {
    const res = await apiClient.get<ApiResponse<UnreadCountDto>>('/messaging/inbox/unread-count');
    return unwrap(res.data);
  },

  async markRead(messagePublicId: string): Promise<InboxMessageDto> {
    const res = await apiClient.patch<ApiResponse<InboxMessageDto>>(
      `/messaging/inbox/${messagePublicId}/read`,
    );
    return unwrap(res.data);
  },

  async searchRecipients(q?: string, role?: 'Driver' | 'PA', limit = 20): Promise<RecipientDto[]> {
    const params: Record<string, string | number> = { limit };
    if (q && q.trim().length > 0) params.q = q.trim();
    if (role) params.role = role;
    const res = await apiClient.get<ApiResponse<RecipientDto[]>>('/messaging/recipients', { params });
    return unwrap(res.data);
  },

  async listThreads(): Promise<ThreadSummaryDto[]> {
    const res = await apiClient.get<ApiResponse<ThreadSummaryDto[]>>('/messaging/threads');
    return unwrap(res.data);
  },

  async getThreadMessages(threadPublicId: string): Promise<ChatMessageDto[]> {
    const res = await apiClient.get<ApiResponse<ChatMessageDto[]>>(`/messaging/threads/${threadPublicId}/messages`);
    return unwrap(res.data);
  },

  async markThreadRead(threadPublicId: string): Promise<{ markedCount: number }> {
    const res = await apiClient.patch<ApiResponse<{ markedCount: number }>>(`/messaging/threads/${threadPublicId}/read`);
    return unwrap(res.data);
  },
};

export const MESSAGING_QUERY_KEY = {
  inbox: ['messaging', 'inbox'] as const,
  unreadCount: ['messaging', 'unread-count'] as const,
  recipients: (q?: string, role?: string) => ['messaging', 'recipients', role ?? 'all', q ?? ''] as const,
  threads: ['messaging', 'threads'] as const,
  threadMessages: (threadPublicId: string) => ['messaging', 'thread', threadPublicId] as const,
};
